import type { ReactNode } from 'react';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router';
import { Button, CircularProgress, Icon } from '@material-ui/core';
import type { Theme } from '@material-ui/core';
import type { ButtonTypeMap } from '@material-ui/core/Button';
import type {
    CommonProps,
    OverridableTypeMap,
} from '@material-ui/core/OverridableComponent';

import { generateRouteParams } from '@klp/routing';

import { Illustration } from '../Illustrations';
import type { IconName } from '../Illustrations';
import { useKlpStyles, removeKlpStyles } from '../Theme';
import type { IKlpStyles } from '../Theme';

export enum ActionButtonType {
    back = 'back',
    cancel = 'cancel',
    forward = 'forward',
    button = 'button',
    submit = 'submit',
    external = 'external',
    save = 'save',
    download = 'download',
}

interface IOwnProps {
    actionType: keyof typeof ActionButtonType;
    translation: string;
    translationOptions?: Record<string, any>;
    target?: string;
    targetParams?: { [prop: string]: string };
    clickHandler?: () => void;
    onClick?: (event: React.MouseEvent<HTMLButtonElement>) => void;
    startIcon?: IconName | ReactNode;
    endIcon?: IconName | ReactNode;
    isLoading?: boolean;
}

export type IActionButtonProps = IOwnProps &
    Partial<ButtonTypeMap['props']> &
    CommonProps<OverridableTypeMap> &
    IKlpStyles;

const styles = (theme: Theme) => ({
    button: {
        display: 'flex',
        borderRadius: theme.spacing(0.2),
        marginLeft: theme.spacing(0.5),
        marginRight: theme.spacing(0.5),
        '&:first-of-type': {
            marginLeft: 0,
        },
        '&:last-of-type': {
            marginRight: 0,
        },
    },
});

const iconMapStart: Partial<Record<ActionButtonType, string>> = {
    back: 'arrow_back',
    cancel: 'clear',
};

const iconMapEnd: Partial<Record<ActionButtonType, string>> = {
    submit: 'check',
    save: 'check',
    external: 'open_in_new_sharp',
    download: 'get_app',
    forward: 'arrow_forward',
};

const colorMap: Partial<
    Record<ActionButtonType, 'primary' | 'secondary' | 'default'>
> = {
    cancel: 'secondary',
};

const isReactNode = (node): node is ReactNode => React.isValidElement(node);

export const ActionButton: React.FC<IActionButtonProps> = (props) => {
    const { t } = useTranslation();
    const history = useHistory();
    const classes = useKlpStyles(props, styles);

    const {
        className,
        actionType,
        translation,
        translationOptions,
        startIcon,
        endIcon,
        disabled,
        target,
        targetParams,
        clickHandler,
        onClick,
        isLoading = false,
        ...rest
    } = props;

    const handleClick = (event: React.MouseEvent<HTMLButtonElement>): void => {
        if (onClick) {
            onClick(event);
        } else if (clickHandler) {
            clickHandler();
        } else if (target) {
            let targetUrl = target;
            if (targetParams) {
                targetUrl = targetUrl.concat(generateRouteParams(targetParams));
            }
            history.push(targetUrl);
        }
    };

    const getIcon = (
        icon: string | ReactNode | undefined,
        type: keyof typeof ActionButtonType,
        position: 'start' | 'end',
    ) => {
        if (isReactNode(icon)) return icon;
        if (type === 'button' && typeof icon === 'string') {
            return <Illustration icon={icon as IconName} size="medium" />;
        }
        if (position === 'start' && iconMapStart[type]) {
            return <Icon>{iconMapStart[type]}</Icon>;
        }
        if (position === 'end' && iconMapEnd[type]) {
            return <Icon>{iconMapEnd[type]}</Icon>;
        }
        return null;
    };

    return (
        <Button
            variant="outlined"
            color={colorMap[actionType] || rest.color || 'primary'}
            startIcon={getIcon(startIcon, actionType, 'start')}
            endIcon={
                isLoading ? (
                    <CircularProgress size={16} />
                ) : (
                    getIcon(endIcon, actionType, 'end')
                )
            }
            onClick={disabled ? undefined : handleClick}
            className={`${classes.button} ${classes.klpStyles} ${className || ''}`}
            disabled={disabled || isLoading}
            type={actionType === 'submit' ? 'submit' : 'button'}
            data-testid={props['data-testid']}
            {...removeKlpStyles(rest)}
        >
            {t(translation, translationOptions)}
        </Button>
    );
};
